import type { Accent } from '../../shared/network';
import { siriusPixelIcon } from '../lib/logos';

/**
 * Pixel-Icons (16×16-Raster, harte Kanten – Design-System §7).
 * Bewusst Inline-SVG statt einer Icon-Library: `shape-rendering: crispEdges`
 * und die Palette bleiben unter Kontrolle.
 */
export const ACCENT_HEX: Record<Accent, string> = {
  pink: '#F25A94',
  teal: '#00E6C7',
  cyan: '#4FB8CD',
  purple: '#8b5cf6',
  magenta: '#E06BE8',
};

type IconName =
  | 'people'
  | 'chart'
  | 'document'
  | 'cube'
  | 'users'
  | 'calculator'
  | 'file-signature'
  | 'quote'
  | 'service'
  | 'tools'
  | 'import'
  | 'grid'
  | 'app';

/** Icons, die als Bilddatei kommen (eigene Farben, Design-System §11). */
const IMAGE_ICONS: Record<string, string> = {
  sirius: siriusPixelIcon,
};

function paths(name: IconName, color: string) {
  switch (name) {
    case 'people':
    case 'users':
      return <path d="M5 2h2v2H5zM9 2h2v2H9zM4 5h8v2H4zM3 8h4v6H3zM9 8h4v6H9z" fill={color} />;
    case 'chart':
      return <path d="M2 12h2v2H2zM5 9h2v5H5zM8 6h2v8H8zM11 3h2v11h-2z" fill={color} />;
    case 'document':
    case 'file-signature':
      return (
        <>
          <path d="M3 1h7l3 3v11H3z" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M5 7h6M5 10h6" stroke={color} strokeWidth="1.6" />
        </>
      );
    case 'cube':
      return (
        <>
          <path d="M8 1l6 3.5v7L8 15l-6-3.5v-7z" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M8 1v7m0 0L2 4.5M8 8l6-3.5" stroke={color} strokeWidth="1.4" />
        </>
      );
    case 'calculator':
      return (
        <>
          <rect x="3" y="1" width="10" height="14" rx="1.5" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M5 4h6M5 8h2M9 8h2M5 11h2M9 11h2" stroke={color} strokeWidth="1.6" />
        </>
      );
    case 'quote':
      return (
        <>
          <path d="M3 2h10v12H3z" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M5 5h6M5 8h6M5 11h3" stroke={color} strokeWidth="1.6" />
        </>
      );
    case 'service':
      // Headset: Service/Support
      return (
        <>
          <path d="M3 9V8a5 5 0 0 1 10 0v1" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M2 9h3v5H2zM11 9h3v5h-3z" fill={color} />
          <path d="M8 14h3" stroke={color} strokeWidth="1.6" />
        </>
      );
    case 'tools':
      // Schraubenschlüssel, diagonal
      return (
        <>
          <path d="M10 2a3 3 0 0 0 3 4l-7 7a2 2 0 1 1-2-2l7-7a3 3 0 0 1-1-2z" fill={color} />
        </>
      );
    case 'import':
      // Pfeil in Ablage: Import
      return (
        <>
          <path d="M8 1v7M5 6l3 3 3-3" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M2 10v4h12v-4" fill="none" stroke={color} strokeWidth="1.6" />
        </>
      );
    default:
      return (
        <>
          <rect x="2" y="2" width="12" height="12" rx="2" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M5 8h6M8 5v6" stroke={color} strokeWidth="1.6" />
        </>
      );
  }
}

export function PixelIcon({
  name,
  accent,
  size,
}: {
  name: string;
  accent: Accent;
  size?: number;
}) {
  const color = ACCENT_HEX[accent];

  const image = IMAGE_ICONS[name];
  if (image) {
    return (
      <img
        className="pixel-img"
        src={image}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
      />
    );
  }

  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      {paths(name as IconName, color)}
    </svg>
  );
}
